import { FloatingComposer } from "@liveblocks/react-ui";
import { ViewportPortal, useStore } from "@xyflow/react";
import { useMemo, type CSSProperties } from "react";

import {
  CanvasPinAffordance,
  PIN_ICON_SIZE,
  PIN_LAYER_Z_INDEX,
} from "../components/CanvasPinAffordance";
import { useQuantizedZoom } from "../hooks/useQuantizedZoom";
import { nodeBoundsById } from "../utils/nodeBounds";
import { CanvasCommentSelfPin } from "./CanvasCommentSelfPin";
import { COMMENT_WINDOW_WIDTH } from "./CanvasCommentThreadWindow";
import { useCanvasComments } from "./CanvasCommentsContext";
import { resolvePinPosition } from "./threadPlacement";

/**
 * New-thread placement: the ghost drop hands over a placement, and the
 * composer opens on it. `metadata` is what stamps the pin at creation —
 * anchor included when the drop snapped to a node, so there is no second
 * write.
 */
export function CanvasCommentComposer() {
  const { draft, cancelPlacement } = useCanvasComments();
  const nodes = useStore((s) => s.nodes);
  const zoom = useQuantizedZoom();

  const position = useMemo(
    () => (draft ? resolvePinPosition(draft, nodeBoundsById(nodes)) : null),
    [draft, nodes],
  );

  if (!draft || !position) return null;

  return (
    <ViewportPortal>
      <div
        style={{
          position: "absolute",
          left: position.x,
          top: position.y,
          pointerEvents: "auto",
          // The draft is a pin like any other, and nodes elevate over it.
          zIndex: PIN_LAYER_Z_INDEX,
        }}
      >
        <FloatingComposer
          defaultOpen
          metadata={draft}
          // The composer portals to <body>, so the theme scope rides its own
          // root rather than an ancestor.
          className="canvas-comments"
          // The draft the composer opens on becomes the thread window; same
          // width, so nothing resizes under the cursor on submit.
          style={
            {
              "--lb-floating-composer-size": `${COMMENT_WINDOW_WIDTH}px`,
            } as CSSProperties
          }
          onComposerSubmit={cancelPlacement}
          onOpenChange={(open) => {
            if (!open) cancelPlacement();
          }}
        >
          {/* Radix anchors the composer to this element's layout rect, and a
              layout rect ignores the descendant bubble's counter-scale. So the
              box is drawn here instead: the pin's own, PIN_ICON_SIZE screen-px
              square straddling (x, y), which in flow units is PIN_ICON_SIZE /
              zoom. The bubble then paints from its top-left, un-centered,
              landing exactly where the centered variant put it. */}
          <div
            style={{
              position: "absolute",
              left: -PIN_ICON_SIZE / 2 / zoom,
              top: -PIN_ICON_SIZE / 2 / zoom,
              width: PIN_ICON_SIZE / zoom,
              height: PIN_ICON_SIZE / zoom,
            }}
          >
            <CanvasPinAffordance
              x={position.x}
              y={position.y}
              open={false}
              bubble={<CanvasCommentSelfPin />}
            />
          </div>
        </FloatingComposer>
      </div>
    </ViewportPortal>
  );
}
